/**
 * applyTargetFps — Phaser target FPS 적용 (부팅 config 생성 + 런타임 best-effort 조정).
 *
 * 부팅: buildFpsConfig(target)를 game config의 `fps`에 넣는다. forceSetTimeOut=true로 RAF 대신
 *       setTimeout 기반 step → 120Hz 패널에서도 60/30으로 확실히 캡(발열↓).
 * 런타임: applyTargetFps(game, fps)가 TimeStep 내부를 best-effort 조정. Phaser 3.80 내부 필드
 *         의존이라 실패할 수 있어 boolean 반환 — 앱은 설정값을 영속해 다음 부팅에서 확실 반영한다.
 */
import type Phaser from "phaser";

export interface BuildFpsConfigOptions {
  /**
   * true면 setTimeout 기반으로 target을 강제한다. false면 RAF를 유지한다.
   * Android WebView처럼 setTimeout pacing이 불안정한 환경은 false로 비교할 수 있다.
   */
  forceSetTimeOut?: boolean;
  /** Phaser smoothStep 사용 여부. 기본 true. */
  smoothStep?: boolean;
}

/** game config.fps에 넣을 FPSConfig. target 미만으로 떨어지면 min에서 보정. */
export function buildFpsConfig(
  targetFps: number,
  opts: BuildFpsConfigOptions = {},
): Phaser.Types.Core.FPSConfig {
  return {
    target: targetFps,
    min: Math.max(10, Math.floor(targetFps / 2)),
    // 주사율 초과 캡을 보장(RAF는 패널 주사율을 따라가므로 setTimeout으로 강제).
    forceSetTimeOut: opts.forceSetTimeOut ?? true,
    smoothStep: opts.smoothStep ?? true,
  };
}

interface TimeStepLike {
  fps?: number;
  _target?: number;
  _min?: number;
  resetDelta?: () => void;
}

/**
 * 런타임 target FPS 조정 (best-effort). 적용되면 true.
 * Phaser 3.80 TimeStep: `fps`=목표 fps, `_target`=프레임당 ms, `_min`=스로틀 하한 ms.
 */
export function applyTargetFps(game: Phaser.Game, fps: number): boolean {
  const loop = game.loop as unknown as TimeStepLike;
  if (!loop || typeof loop.fps !== "number") return false;
  loop.fps = fps;
  loop._target = 1000 / fps;
  loop._min = 1000 / Math.max(10, Math.floor(fps / 2));
  loop.resetDelta?.();
  return true;
}
